<template>
  <div id="app" class="h-100" :class="[skinClasses, {'medium-screen': mediumScreen, 'big-screen': bigScreen}]">
    <component :is="layout" v-if="displaySystem">
      <router-view />
    </component>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { ref, watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";
import updateDomHelper from "./helpers/update-dom-helper";

import FingerprintJS from "@fingerprintjs/fingerprintjs";
import localStorageHelper from './helpers/local-storage-helper';
import { ENUM_LOCALE_LIST } from './helpers/locale-helper';
import userService from './providers/userService';
import Vue from 'vue';
import authService from './providers/authService';

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");
const LayoutMain = () => import("@/layouts/main/LayoutMain.vue");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    LayoutMain,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      if (this.$route.meta.layout === "main") return "layout-main";

      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
    mediumScreen() {
      return this.windowWidth > 1200 && this.windowWidth < 1300;
    },
    bigScreen() {
      return this.windowWidth > 1300;
    }
  },
  async beforeCreate() {
    if (process.env.NODE_ENV == 'production') {
      Vue.prototype.$config = await authService.getConfig(process.env.BASE_URL + "config.json", {auth: {username: 'publisher-user', password: 'ccb4d652e79d210ff834e4de956bd7d3'}});
    } else {
      const configResp = await fetch(process.env.BASE_URL + "config.json");
      Vue.prototype.$config = await configResp.json();
    }
    // console.log('[loadconfig]', this.$config);
    this.displaySystem = true;
    
    // update localstorage data to store
    // this.$store.dispatch('auth/updateDataLocalStorage');

    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    /* eslint-disable */
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    /* eslint-disable */
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");

    // INIT DRAFT NEWS
    // this.$store.dispatch('manageNews/initDraftNews');

    
    // SET THEME
    if (this.$store.state.auth.isAuthenticated) {
      // store.commit("auth/UPDATE_USERCORE_DATA");
      // UPDATE CURRENT USER DATA FROM SERVER
        const currentUser = localStorageHelper.getCurrentUser();
      try {
        const userConfig = await userService.getUserConfig(new URLSearchParams());
        currentUser.sendNewsConfig = userConfig.sendNewsConfig;
        currentUser.theme = userConfig.theme;
        currentUser.zoom = userConfig.zoom;
        currentUser.locale = userConfig.locale;
        currentUser.subjectViewType = userConfig.subjectViewType;
        currentUser.agency = userConfig.agency;
        currentUser.profile = userConfig.profile;
        currentUser.channels = userConfig.channels;
        currentUser.template = userConfig.template;
      } catch (error) {
        console.log(error);
      }

      updateDomHelper.updateTheme(currentUser.theme);
      updateDomHelper.updateZoom(currentUser.zoom);
      // update theme
      this.$store.dispatch("auth/updateCurrentUser", currentUser);
      const locale = currentUser.locale;
      if (locale) {
        this.$i18n.locale = ENUM_LOCALE_LIST.find(item => item.id == locale).locale;
      }
    }

    const fpPromise = FingerprintJS.load();
    fpPromise
      .then((fp) => fp.get())
      .then((fp) => {
        // This is the visitor identifier:
        localStorageHelper.updateVisitorId(fp.visitorId);
      });
  },
  setup() {
    const displaySystem = ref(false);
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });
    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
      windowWidth,
      displaySystem
    };
  },
};
</script>
